import React, { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, Edit2, CheckCircle, Zap } from 'lucide-react';
import storageService from '../../services/storageService';

const RuleTypes = () => {
  const [ruleTypes, setRuleTypes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRuleType, setEditingRuleType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    value: '',
    description: '',
    icon: 'shield',
    color: 'blue',
    hasPattern: false,
    hasRange: false,
    hasLength: false,
    hasCustom: false
  });

  useEffect(() => {
    loadRuleTypes();
  }, []);

  const loadRuleTypes = () => {
    const data = storageService.getRuleTypes();
    setRuleTypes(data);
  };

  const handleOpenModal = (ruleType = null) => {
    if (ruleType) {
      setEditingRuleType(ruleType);
      setFormData(ruleType);
    } else {
      setEditingRuleType(null);
      setFormData({
        name: '',
        value: '',
        description: '',
        icon: 'shield',
        color: 'blue',
        hasPattern: false,
        hasRange: false,
        hasLength: false,
        hasCustom: false
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRuleType(null);
    setFormData({
      name: '',
      value: '',
      description: '',
      icon: 'shield',
      color: 'blue',
      hasPattern: false,
      hasRange: false,
      hasLength: false,
      hasCustom: false
    });
  };

  const handleSave = () => {
    const ruleType = {
      ...formData,
      id: editingRuleType?.id || `rt${Date.now()}`,
      createdAt: editingRuleType?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    storageService.saveRuleType(ruleType);
    loadRuleTypes();
    handleCloseModal();
  };

  const handleDelete = (id) => {
    if (window.confirm('Xóa loại rule này?')) {
      storageService.deleteRuleType(id);
      loadRuleTypes();
    }
  };

  const iconOptions = [
    { value: 'shield', label: 'Shield', icon: Shield },
    { value: 'check', label: 'Check Circle', icon: CheckCircle },
    { value: 'zap', label: 'Zap', icon: Zap }
  ];

  const colorOptions = [
    { value: 'blue', label: 'Xanh dương', color: '#3B82F6' },
    { value: 'green', label: 'Xanh lá', color: '#10B981' },
    { value: 'purple', label: 'Tím', color: '#8B5CF6' },
    { value: 'orange', label: 'Cam', color: '#F59E0B' },
    { value: 'red', label: 'Đỏ', color: '#EF4444' },
    { value: 'pink', label: 'Hồng', color: '#EC4899' }
  ];

  const getIconComponent = (iconValue) => {
    const iconObj = iconOptions.find(opt => opt.value === iconValue);
    return iconObj ? iconObj.icon : Shield;
  };

  const getColorValue = (colorValue) => {
    const colorObj = colorOptions.find(opt => opt.value === colorValue);
    return colorObj ? colorObj.color : '#3B82F6';
  };

  const defaultRuleTypes = [
    { name: 'Bắt buộc (Required)', value: 'required', isDefault: true, color: 'red' },
    { name: 'Kiểu dữ liệu (Data Type)', value: 'dataType', isDefault: true, color: 'blue' },
    { name: 'Khoảng giá trị (Range)', value: 'range', isDefault: true, color: 'green' },
    { name: 'Định dạng (Regex)', value: 'regex', isDefault: true, color: 'purple' },
    { name: 'Độ dài (Length)', value: 'length', isDefault: true, color: 'orange' },
    { name: 'Tùy chỉnh (Custom)', value: 'custom', isDefault: true, color: 'pink' }
  ];

  return (
    <div className="content-section">
      <div className="section-header">
        <div>
          <h2>
            <Shield size={28} />
            Loại Validation Rule
          </h2>
          <p>Định nghĩa các loại rule base có thể tái sử dụng</p>
        </div>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <Plus size={20} /> Thêm loại rule
        </button>
      </div>

      <div className="card-grid">
        {/* Default Rule Types (Read-only) */}
        <div style={{gridColumn: '1 / -1', marginBottom: '1rem'}}>
          <h3 style={{fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1rem'}}>
            Loại rule mặc định
          </h3>
        </div>
        {defaultRuleTypes.map((ruleType, idx) => {
          const IconComponent = Shield;
          const color = getColorValue(ruleType.color);
          
          return (
            <div key={`default-${idx}`} className="card">
              <div className="card-header">
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius)',
                  background: `linear-gradient(135deg, ${color}15 0%, ${color}25 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <IconComponent size={24} style={{color: color}} />
                </div>
                <h3>{ruleType.name}</h3>
                <span className="badge badge-info" style={{marginTop: '0.5rem'}}>Mặc định</span>
              </div>
              <div className="card-body">
                <div style={{fontSize: '0.875rem', color: 'var(--text-light)'}}>
                  <code style={{background: 'var(--gray-100)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-sm)'}}>
                    {ruleType.value}
                  </code>
                </div>
              </div>
            </div>
          );
        })}

        {/* Custom Rule Types */}
        {ruleTypes.length > 0 && (
          <>
            <div style={{gridColumn: '1 / -1', marginTop: '2rem', marginBottom: '1rem'}}>
              <h3 style={{fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1rem'}}>
                Loại rule tùy chỉnh ({ruleTypes.length})
              </h3>
            </div>
            {ruleTypes.map((ruleType) => {
              const IconComponent = getIconComponent(ruleType.icon);
              const color = getColorValue(ruleType.color);
              
              return (
                <div key={ruleType.id} className="card">
                  <div className="card-header">
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: 'var(--radius)',
                      background: `linear-gradient(135deg, ${color}15 0%, ${color}25 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '0.75rem'
                    }}>
                      <IconComponent size={24} style={{color: color}} />
                    </div>
                    <h3>{ruleType.name}</h3>
                    <p>{ruleType.description}</p>
                  </div>
                  <div className="card-body">
                    <div style={{fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.75rem'}}>
                      <strong>Value:</strong>{' '}
                      <code style={{background: 'var(--gray-100)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-sm)'}}>
                        {ruleType.value}
                      </code>
                    </div>
                    {(ruleType.hasPattern || ruleType.hasRange || ruleType.hasLength || ruleType.hasCustom) && (
                      <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem'}}>
                        {ruleType.hasPattern && <span className="badge badge-info">Pattern</span>}
                        {ruleType.hasRange && <span className="badge badge-success">Range</span>}
                        {ruleType.hasLength && <span className="badge badge-warning">Length</span>}
                        {ruleType.hasCustom && <span className="badge badge-secondary">Custom</span>}
                      </div>
                    )}
                  </div>
                  <div className="card-footer">
                    <button 
                      className="btn-icon" 
                      onClick={() => handleOpenModal(ruleType)}
                      title="Sửa"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      className="btn-icon btn-danger" 
                      onClick={() => handleDelete(ruleType.id)}
                      title="Xóa"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {ruleTypes.length === 0 && (
          <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-light)'}}>
            <Shield size={48} style={{opacity: 0.3, marginBottom: '1rem'}} />
            <p>Chưa có loại rule tùy chỉnh nào</p>
            <button className="btn btn-primary" onClick={() => handleOpenModal()} style={{marginTop: '1rem'}}>
              <Plus size={16} /> Thêm loại rule đầu tiên
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Shield size={24} />
                {editingRuleType ? 'Sửa Loại Rule' : 'Thêm Loại Rule'}
              </h3>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Tên loại rule <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="VD: Định dạng email"
                  />
                </div>

                <div className="form-group">
                  <label>Value (code) <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder="VD: email_format"
                  />
                  <small>Giá trị này sẽ được lưu trong database</small>
                </div>
              </div>

              <div className="form-group">
                <label>Mô tả</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Mô tả về loại rule này"
                  rows={2}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Icon</label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  >
                    {iconOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Màu sắc</label>
                  <select
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  >
                    {colorOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Cấu hình fields (các trường cần thiết khi sử dụng rule này)</label>
                <div className="checkbox-grid">
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.hasPattern}
                      onChange={(e) => setFormData({ ...formData, hasPattern: e.target.checked })}
                    />
                    <span>🔍 Pattern (Regex)</span>
                  </label>
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.hasRange}
                      onChange={(e) => setFormData({ ...formData, hasRange: e.target.checked })}
                    />
                    <span>📊 Range (Min/Max)</span>
                  </label>
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.hasLength}
                      onChange={(e) => setFormData({ ...formData, hasLength: e.target.checked })}
                    />
                    <span>📏 Length (Độ dài)</span>
                  </label>
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.hasCustom}
                      onChange={(e) => setFormData({ ...formData, hasCustom: e.target.checked })}
                    />
                    <span>⚙️ Custom Config</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Hủy
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={!formData.name || !formData.value}
              >
                {editingRuleType ? 'Cập nhật' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuleTypes;
